import { useEffect, useRef } from "react";
import { SOCKET_RETRY_DELAY_MS } from "../portalChat.constants";
import io from "socket.io-client";
import { getBackendUrl } from "@Utils";
import type { ChatMessage } from "../api/portalChat.api";

export type PortalChatSocketEvents = {
  onMessageNew?: (payload: { conversationId: string; message: ChatMessage }) => void;
  onMessageEdited?: (payload: { conversationId: string; message: ChatMessage }) => void;
  onMessageDeleted?: (payload: { conversationId: string; messageId: string }) => void;
  onReactionUpdated?: (payload: {
    conversationId: string;
    messageId: string;
    reactions: { emoji: string; users: string[] }[];
  }) => void;
  onReadUpdated?: (payload: { conversationId: string; userId: string; lastReadDate: number }) => void;
  onConversationUpdated?: (payload: { conversationId: string }) => void;
  onMemberRemoved?: (payload: { conversationId: string }) => void;
  onTyping?: (payload: { conversationId: string; userId: string; isTyping: boolean }) => void;
  onConnectionChange?: (connected: boolean) => void;
};

/**
 * One authenticated socket to the server's `/portal-chat` namespace.
 *
 * Auth rides on the session cookie: the API host is same-site, and the websocket-only transport
 * skips the polling/CORS path, so the `jwtToken` cookie flows on the upgrade request and the
 * namespace middleware validates it exactly like the HTTP `authorize` middleware.
 *
 * Handlers are kept in a ref so consumers can pass fresh closures every render without
 * reconnecting the socket.
 */
export function usePortalChatSocket(enabled: boolean, events: PortalChatSocketEvents) {
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const socketRef = useRef<ReturnType<typeof io> | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const socket = io(`${getBackendUrl()}/portal-chat`, {
      transports: ["websocket"],
      withCredentials: true,
      // Dedicated Manager: without this, socket.io reuses one engine per origin and whichever of
      // this socket / the legacy Collaboration socket connects first silently dictates the
      // transports/withCredentials of the other.
      forceNew: true,
    });
    socketRef.current = socket;

    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    socket.on("connect", () => eventsRef.current.onConnectionChange?.(true));
    socket.on("disconnect", () => eventsRef.current.onConnectionChange?.(false));
    socket.on("connect_error", (error: Error) => {
      eventsRef.current.onConnectionChange?.(false);
      // A namespace-middleware denial is terminal for socket.io-client (no auto-reconnect). The
      // server names transient infra failures "retry"; retry those (and any other non-"unauthorized"
      // denial) with a slow manual reconnect so one Mongo/Redis blip at page load doesn't kill
      // realtime for the whole tab. Genuine "unauthorized" stays terminal.
      if (!socket.active && error.message !== "unauthorized") {
        clearTimeout(retryTimer);
        retryTimer = setTimeout(() => socket.connect(), SOCKET_RETRY_DELAY_MS);
      }
    });
    socket.on("message:new", (payload) => eventsRef.current.onMessageNew?.(payload));
    socket.on("message:edited", (payload) => eventsRef.current.onMessageEdited?.(payload));
    socket.on("message:deleted", (payload) => eventsRef.current.onMessageDeleted?.(payload));
    socket.on("reaction:updated", (payload) => eventsRef.current.onReactionUpdated?.(payload));
    socket.on("read:updated", (payload) => eventsRef.current.onReadUpdated?.(payload));
    socket.on("conversation:updated", (payload) => eventsRef.current.onConversationUpdated?.(payload));
    socket.on("member:removed", (payload) => eventsRef.current.onMemberRemoved?.(payload));
    socket.on("typing", (payload) => eventsRef.current.onTyping?.(payload));

    return () => {
      clearTimeout(retryTimer);
      socketRef.current = null;
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [enabled]);

  const emitTyping = (conversationId: string, isTyping: boolean) => {
    // Identity is server-side (the authenticated socket) — no display name travels with the event.
    socketRef.current?.emit("typing", { conversationId, isTyping });
  };

  return { emitTyping };
}
